import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import TemplatesIntro from "pages/missionTemplates/components/TemplatesIntro";
import SubNavbar from "components/SubNavbar/SubNavbar";
import PageForm from "components/pageForm";
import Button from "components/button";
import StyledInput from "components/styledInput/StyledInput";
import EmptyStateCompany from "components/emptyState";
import TemplateRow, { CreateTemplateRow, ListTag } from "pages/missionTemplates/components/TemplateRow";
import TemplateRowSkeleton from "pages/missionTemplates/components/TemplateRowSkeleton";
import UseContext from "hooks/useContext";
import useFetchData from "hooks/useFetchData";
import ApiDataBase from "server";
import { Plus, SearchIcon } from "assets/icons";
import { filterMissionTemplates } from "pages/missionTemplates/utils/templateListing";

const SKELETON_ROWS = [1, 2, 3, 4, 5];

const MissionTemplates = () => {
    useFetchData();
    const navigate = useNavigate();
    const {
        missionTemplates,
        setMissionTemplates,
        refreshMissionTemplates,
        setPreviewMissionTemplate,
        setModalType,
        setModalShow,
        resetTemplateData,
        companyData,
        missionTemplatesCompanyId,
        setMissionTemplatesCompanyId,
        language,
    } = UseContext();
    const [isLoading, setIsLoading] = useState(() => {
        if (companyData?._id == null) return true;
        return missionTemplatesCompanyId !== String(companyData._id);
    });
    const [searchQuery, setSearchQuery] = useState("");
    const hasCompanyLoaded = companyData?._id != null;
    const scopeIdRef = useRef(null);
    scopeIdRef.current = missionTemplatesCompanyId;
    const currentCompanyIdRef = useRef(null);
    currentCompanyIdRef.current =
        companyData?._id != null ? String(companyData._id) : null;

    useEffect(() => {
        if (!companyData?._id) return undefined;
        const requestedId = String(companyData._id);
        const hasCacheForCompany = scopeIdRef.current === requestedId;
        setIsLoading(!hasCacheForCompany);

        let cancelled = false;

        ApiDataBase.getMissionTemplates(
            { idCompany: requestedId },
            (res) => {
                if (cancelled) return;
                if (requestedId !== currentCompanyIdRef.current) return;

                const list = res?.templates || [];
                setMissionTemplates(list);
                setMissionTemplatesCompanyId(requestedId);
                setIsLoading(false);
            },
            (err) => {
                if (cancelled) return;
                console.error(err);
                toast.error("Impossible de charger les fiches de poste");
                setIsLoading(false);
            }
        );

        return () => {
            cancelled = true;
        };
    }, [refreshMissionTemplates, companyData?._id, setMissionTemplates, setMissionTemplatesCompanyId]);

    const visibleTemplates = useMemo(
        () => filterMissionTemplates(missionTemplates, searchQuery),
        [missionTemplates, searchQuery]
    );

    const handleCreate = () => {
        resetTemplateData();
        navigate("/missionTemplates/create");
    };

    const handlePreview = (template) => {
        setPreviewMissionTemplate(template);
        setModalType("missionTemplate_preview");
        setModalShow(true);
    };

    const isListLoading = !hasCompanyLoaded || isLoading;
    const hasTemplates = missionTemplates && missionTemplates.length > 0;

    const label = (key, fallback) => language.getValue(`missionTemplate.list.${key}`, fallback);

    const renderList = () => {
        if (isListLoading) {
            return (
                <div className="overflow-hidden rounded-lg border border-gray-100 bg-white" role="status" aria-busy="true">
                    <span className="sr-only">{label("loading", "Chargement des fiches de poste…")}</span>
                    {SKELETON_ROWS.map((key) => (
                        <TemplateRowSkeleton key={key} />
                    ))}
                </div>
            );
        }
        if (visibleTemplates.length === 0) {
            return (
                <p className="py-12 text-center ft-sm text-gray-500" role="status">
                    {label("noResult", "Aucune fiche de poste ne correspond à votre recherche.")}
                </p>
            );
        }
        return (
            <div className="overflow-hidden rounded-lg border border-gray-100 bg-white">
                {visibleTemplates.map((template) => (
                    <TemplateRow key={template._id} template={template} onPreview={handlePreview} />
                ))}
                <CreateTemplateRow label={label("createRow", "Créer une fiche de poste")} onCreate={handleCreate} />
            </div>
        );
    };

    // Figma "Fiches de poste" page (node 812:4759): search and create in the SubNavbar, as on the
    // other lists, then the introduction card and the list section.
    return (
        <PageForm>
            <SubNavbar>
                <div className="w-full h-full flex items-center justify-between gap-4">
                    <div className="w-[350px] max-w-full">
                        <StyledInput
                            type={"text"}
                            value={searchQuery}
                            onChange={(event) => setSearchQuery(event.target.value)}
                            isDisabled={isListLoading}
                            placeholder={label("searchPlaceholder", "Rechercher une fiche de poste")}
                            imgFront={<SearchIcon wh={20} color="#9CA3AF" />}
                        />
                    </div>
                    <Button color={"PRIMARY"} size={"SM"} onClick={handleCreate}>
                        <span aria-hidden="true">
                            <Plus wh={12} color={"#FFFFFF"} />
                        </span>
                        {label("create", "Fiche de poste")}
                    </Button>
                </div>
            </SubNavbar>
            <div className="flex flex-col gap-6 px-8 pb-8 pt-6">
                {/* List on the left, the introduction on its right (above it on a narrow screen). */}
                <div className="flex flex-col-reverse gap-6 ls:flex-row ls:items-start">
                    <div className="min-w-0 flex-1">
                        {!isListLoading && !hasTemplates ? (
                            <EmptyStateCompany type={"job_post_template"} onAction={handleCreate} compact />
                        ) : (
                            <section className="flex flex-col gap-3">
                                <div className="flex items-center gap-2">
                                    <h2 className="text-base font-medium leading-6 text-gray-900">
                                        {label("sectionTitle", "Vos fiches de poste")}
                                    </h2>
                                    {!isListLoading ? <ListTag>{missionTemplates.length}</ListTag> : null}
                                    <span className="flex-1" />
                                    <p className="hidden text-xs leading-4 text-gray-700 md:block">
                                        {label("sectionHint", "Configurez une fiche une fois, réutilisez-la à chaque mission")}
                                    </p>
                                </div>
                                {renderList()}
                            </section>
                        )}
                    </div>
                    <TemplatesIntro
                        eyebrow={label("banner.eyebrow", "Nouveau")}
                        title={label("banner.title", "Créez vos missions en quelques clics")}
                        description={label(
                            "banner.description",
                            "Une fiche de poste décrit un poste une bonne fois pour toutes. Vos missions partent ensuite de la fiche."
                        )}
                        benefits={[
                            { text: label("banner.benefitTasks", "Tâches et prérequis déjà remplis"), badge: "check" },
                            {
                                text: label("banner.benefitDressCode", "Dress code et matériel connus de l'intérimaire"),
                                badge: "check",
                            },
                            { text: label("banner.benefitTime", "Moins de saisie à chaque nouvelle mission"), badge: "check" },
                        ]}
                        closeLabel={label("banner.close", "Fermer")}
                        className="w-full shrink-0 ls:sticky ls:top-[150px] ls:w-[340px]"
                    />
                </div>
            </div>
        </PageForm>
    );
};

export default MissionTemplates;
